import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SelectChangeEvent, useMediaQuery, useTheme } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { FuelType } from '@/types/price-comparison.types.ts';
import { StationsSortType } from '@/types/common.types.ts';
import { useSnackbar, useScrollSync } from '@/hooks';
import { useExportProvinceDataMutation, useGetProvinceStationsDataQuery } from '@/services';
import { PerformanceStatusIndicator, ErrorState, LoadingTable } from '@/components';
import { useRolePermissions } from '@/hooks/useRolePermissions';

import { StationsPageHeader, StationsPageControls, AreaStationsTable, StationsTableHeader } from '../../components';

import './ProvinceStationsPage.css';

function ProvinceStationsPage() {
  const { province } = useParams<{ province: string }>();
  const navigate = useNavigate();

  const { setSnackbarMessage } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canViewSales, canExport } = useRolePermissions();
  const [sortBy, setSortBy] = useState<StationsSortType>('alphabetical');
  const [selectedBrand, setSelectedBrand] = useState('');
  const queryArgs = province ? { includeDiscounts: true, province, sortBy, brandFilter: selectedBrand } : skipToken;
  const defulatStationData = { region: '', stations: [], totalStations: 0 };
  const { data, isLoading, isFetching, isSuccess, isError, refetch } = useGetProvinceStationsDataQuery(queryArgs, {
    refetchOnMountOrArgChange: true
  });
  const {
    stationsData = defulatStationData,
    stationsFuelTypes = [],
    brands: availableBrands = [],
    performanceStatus
  } = data || {};
  const fuelTypes = stationsFuelTypes.map((fuel: FuelType): string => fuel.fuel_type);

  const [exportProvinceData] = useExportProvinceDataMutation();
  useEffect(() => {
    if (isError) {
      setSnackbarMessage('Помилка завантаження даних станцій', 'error');
      //  showSnackbarMessage(`Помилка завантаження даних станцій: ${error?.message || 'Unknown error'}`, 'error');
    }
  }, [isError, setSnackbarMessage]);

  const { headerRef, bodyRef } = useScrollSync();

  const handleSortBy = (event: SelectChangeEvent<StationsSortType>) => {
    setSortBy(event.target.value as StationsSortType);
  };

  const handleBrandChange = (event: SelectChangeEvent<string>) => {
    setSelectedBrand(event.target.value);
  };

  const exportProvincePrices = async () => {
    if (!province) return;
    await exportProvinceData({ province })
      .unwrap()
      .then(() => setSnackbarMessage(`Файл для регіону ${province} успішно вивантажено`, 'success'))
      .catch(() => setSnackbarMessage('Помилка вивантаження файлу', 'error'));
  };
  const retryLoad = () => {
    refetch();
  };

  if (isError) {
    return (
      <div className="regional-stations-container">
        <ErrorState message="Помилка завантаження станцій" onRetry={retryLoad} />
      </div>
    );
  }

  if (isLoading || isFetching || !stationsData) {
    return <LoadingTable message="Завантаження станцій..." rows={10} columns={15} />;
  }

  return (
    <div className="regional-stations-container">
      {isSuccess && stationsData && (
        <PerformanceStatusIndicator performanceStatus={performanceStatus} onRefreshNeeded={retryLoad} />
      )}

      {/* Sticky Header */}
      <div className="regional-stations-sticky-header">
        <StationsPageHeader
          title={`Детальний перегляд АЗС - ${province}`}
          backPath="/pricing-province"
          backLabel="Назад до динаміки цін"
          isMobile={isMobile}
          onNavigate={navigate}
        />
        <StationsPageControls
          sortBy={sortBy}
          brandFilter={selectedBrand}
          availableBrands={availableBrands}
          isMobile={isMobile}
          onSortChange={handleSortBy}
          onBrandChange={handleBrandChange}
          onExport={exportProvincePrices}
          showExport={canExport}
          exportButtonClass="province-stations-export-button"
        />
        <StationsTableHeader
          fuelTypes={fuelTypes}
          isMobile={isMobile}
          tableClass="province-stations-table"
          scrollRef={headerRef}
          hideSales={!canViewSales}
        />
      </div>

      <AreaStationsTable
        stationsData={stationsData}
        fuelTypes={fuelTypes}
        isMobile={isMobile}
        sortBy={sortBy}
        selectedBrand={selectedBrand}
        tableClass="province-stations-table"
        scrollRef={bodyRef}
        hideSales={!canViewSales}
      />
    </div>
  );
}

export default ProvinceStationsPage;
